import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  CheckCircle2,
  Loader2,
  Plus,
  Save,
  ShieldCheck,
  Trash2,
  UsersRound
} from 'lucide-react';
import RbacModal from '../../components/admin/RbacModal';
import { employerRbacAPI } from '../../services/api';
import {
  EMPLOYER_PERMISSIONS,
  getPermissionLabel,
  getPermissionsForDisplay
} from '../../utils/rbac';

const editablePermissionKeys = [
  EMPLOYER_PERMISSIONS.DASHBOARD,
  EMPLOYER_PERMISSIONS.REPORTS,
  EMPLOYER_PERMISSIONS.JOBS,
  EMPLOYER_PERMISSIONS.CANDIDATES,
  EMPLOYER_PERMISSIONS.INTERVIEWS,
  EMPLOYER_PERMISSIONS.OFFERS,
  EMPLOYER_PERMISSIONS.ASSESSMENTS,
  EMPLOYER_PERMISSIONS.CONFIGURATION,
  EMPLOYER_PERMISSIONS.PROFILE
];

const emptyForm = {
  id: null,
  name: '',
  description: '',
  permissions: [
    EMPLOYER_PERMISSIONS.DASHBOARD,
    EMPLOYER_PERMISSIONS.JOBS,
    EMPLOYER_PERMISSIONS.CANDIDATES
  ]
};

const extractError = (error, fallback) =>
  error?.message || error?.error || error?.response?.error || fallback;

function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const editingRole = useMemo(
    () => roles.find((role) => Number(role.id) === Number(form.id)),
    [roles, form.id]
  );
  const isEditingSuperAdmin = Boolean(editingRole?.isSuperAdmin);
  const canDeleteEditingRole = Boolean(
    editingRole &&
      !editingRole.isSuperAdmin &&
      Number(editingRole.userCount || 0) === 0
  );

  const loadRoles = async () => {
    setLoading(true);
    try {
      const response = await employerRbacAPI.getRoles();
      setRoles(response.roles || []);
    } catch (error) {
      toast.error(extractError(error, 'Failed to load roles.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  const closeEditor = () => {
    if (saving || deleting) return;
    setEditorOpen(false);
    setConfirmingDelete(false);
    setForm(emptyForm);
  };

  const startCreate = () => {
    setForm(emptyForm);
    setConfirmingDelete(false);
    setEditorOpen(true);
  };

  const startEdit = (role) => {
    setForm({
      id: role.id,
      name: role.name || '',
      description: role.description || '',
      permissions: role.permissions || []
    });
    setConfirmingDelete(false);
    setEditorOpen(true);
  };

  const togglePermission = (permission) => {
    if (isEditingSuperAdmin) return;
    setForm((current) => {
      const exists = current.permissions.includes(permission);
      return {
        ...current,
        permissions: exists
          ? current.permissions.filter((item) => item !== permission)
          : [...current.permissions, permission]
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        permissions: form.permissions
      };

      if (form.id) {
        await employerRbacAPI.updateRole(form.id, payload);
        toast.success('Role updated.');
      } else {
        await employerRbacAPI.createRole(payload);
        toast.success('Role created.');
      }

      setEditorOpen(false);
      setForm(emptyForm);
      await loadRoles();
    } catch (error) {
      toast.error(extractError(error, 'Role save failed.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canDeleteEditingRole || deleting) return;

    setDeleting(true);
    try {
      await employerRbacAPI.deleteRole(editingRole.id);
      toast.success('Role deleted.');
      setEditorOpen(false);
      setConfirmingDelete(false);
      setForm(emptyForm);
      await loadRoles();
    } catch (error) {
      toast.error(extractError(error, 'Role delete failed.'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white px-4 py-6 pt-16 sm:px-6 sm:pt-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full bg-[#eef9ff] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-sky-700">
              <ShieldCheck size={14} /> Access control
            </p>
            <h1 className="mt-3 font-display text-3xl font-normal text-[#0b1f3a]">
              Roles
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
              Review role coverage at a glance, then open one role when you need
              to change its permissions.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <Link
              to="/users"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-[#0b1f3a] shadow-sm transition hover:border-sky-200 hover:bg-[#eef9ff]"
            >
              <UsersRound size={16} />
              Users
            </Link>
            <button
              type="button"
              onClick={startCreate}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-sky-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-sky-700"
            >
              <Plus size={16} />
              New role
            </button>
          </div>
        </header>

        <section className="overflow-hidden rounded-lg border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
          <div className="flex flex-col gap-2 border-b border-slate-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <div>
              <h2 className="font-display text-lg font-normal text-[#0b1f3a]">
                Configured roles
              </h2>
              <p className="mt-1 text-xs text-slate-500">
                {roles.length} roles available to this employer account
              </p>
            </div>
            <p className="text-xs leading-5 text-slate-500">
              Roles and Users access remain Super admin only.
            </p>
          </div>

          {loading ? (
            <div className="flex min-h-[280px] items-center justify-center text-slate-500">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Loading roles
            </div>
          ) : roles.length ? (
            <div className="overflow-x-auto">
              <table className="min-w-[900px] divide-y divide-slate-100 text-left text-sm">
                <thead className="bg-slate-50/80 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  <tr>
                    <th className="px-5 py-3">Role</th>
                    <th className="px-5 py-3">Accessible areas</th>
                    <th className="px-5 py-3">Users</th>
                    <th className="px-5 py-3">Type</th>
                    <th className="px-5 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {roles.map((role) => {
                    const permissions = getPermissionsForDisplay(
                      role.permissions || []
                    );
                    return (
                      <tr
                        key={role.id}
                        className="align-top transition hover:bg-slate-50/60"
                      >
                        <td className="px-5 py-4">
                          <div className="font-medium text-[#0b1f3a]">
                            {role.name}
                          </div>
                          <div className="mt-1 max-w-xs text-xs leading-5 text-slate-500">
                            {role.description || 'No role description added.'}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex max-w-xl flex-wrap gap-1.5">
                            {permissions.slice(0, 5).map((permission) => (
                              <span
                                key={permission}
                                className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600"
                              >
                                {getPermissionLabel(permission)}
                              </span>
                            ))}
                            {permissions.length > 5 && (
                              <span className="rounded-md bg-[#eef9ff] px-2 py-1 text-xs font-semibold text-sky-700">
                                +{permissions.length - 5} more
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-slate-600">
                          {role.userCount || 0}
                        </td>
                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                              role.isSuperAdmin
                                ? 'bg-sky-50 text-sky-700'
                                : role.isSystem
                                ? 'bg-slate-100 text-slate-600'
                                : 'bg-emerald-50 text-emerald-700'
                            }`}
                          >
                            {role.isSuperAdmin
                              ? 'Super admin'
                              : role.isSystem
                              ? 'Default'
                              : 'Custom'}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => startEdit(role)}
                            className="inline-flex h-9 items-center justify-center rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-[#0b1f3a] transition hover:border-sky-200 hover:bg-[#eef9ff]"
                          >
                            Manage
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-5 py-12 text-center">
              <ShieldCheck className="mx-auto text-slate-300" size={30} />
              <p className="mt-3 text-sm font-medium text-slate-700">
                No roles configured
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Create the first dashboard role to define access.
              </p>
            </div>
          )}
        </section>
      </div>

      <RbacModal
        open={editorOpen}
        onClose={closeEditor}
        eyebrow="Role management"
        title={
          confirmingDelete
            ? `Delete ${editingRole?.name || 'role'}?`
            : form.id
            ? 'Manage role'
            : 'Create role'
        }
        description={
          confirmingDelete
            ? 'This permanently removes the role. This action cannot be undone.'
            : 'Choose the dashboard areas this role can open. Changes apply to every user assigned to the role.'
        }
        maxWidth="max-w-3xl"
        footer={
          confirmingDelete ? (
            <>
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                disabled={deleting}
                className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:opacity-60"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-rose-600 px-4 text-sm font-medium text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deleting ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Trash2 size={16} />
                )}
                Delete role
              </button>
            </>
          ) : (
            <>
              <div>
                {form.id && (
                  <button
                    type="button"
                    onClick={() => setConfirmingDelete(true)}
                    disabled={!canDeleteEditingRole}
                    title={
                      Number(editingRole?.userCount || 0) > 0
                        ? 'Assigned roles cannot be deleted'
                        : editingRole?.isSuperAdmin
                        ? 'The Super admin role cannot be deleted'
                        : 'Delete role'
                    }
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-rose-200 bg-white px-4 text-sm font-medium text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                )}
              </div>
              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={closeEditor}
                  disabled={saving}
                  className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="role-editor-form"
                  disabled={
                    saving ||
                    !form.name.trim() ||
                    (!isEditingSuperAdmin && form.permissions.length === 0)
                  }
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-sky-600 px-4 text-sm font-medium text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Save size={16} />
                  )}
                  {form.id ? 'Save changes' : 'Create role'}
                </button>
              </div>
            </>
          )
        }
      >
        {confirmingDelete ? (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-4 text-sm leading-6 text-rose-800">
            <strong>{editingRole?.name}</strong> has no assigned users and can
            be removed. You will need to recreate it if this access policy is
            needed later.
          </div>
        ) : (
          <form
            id="role-editor-form"
            onSubmit={handleSubmit}
            className="space-y-5"
          >
            <div className={`grid gap-4 ${form.id ? 'sm:grid-cols-2' : ''}`}>
              <label className="block">
                <span className="mb-2 block text-sm font-medium text-slate-700">
                  Role name
                </span>
                <input
                  value={form.name}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      name: event.target.value
                    }))
                  }
                  className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                  placeholder="Hiring lead"
                  required
                />
              </label>

              {form.id && (
                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-slate-700">
                    Assigned users
                  </span>
                  <input
                    value={`${editingRole?.userCount || 0} users`}
                    className="h-11 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-500 outline-none"
                    disabled
                  />
                </label>
              )}
            </div>

            <label className="block">
              <span className="mb-2 block text-sm font-medium text-slate-700">
                Description
              </span>
              <textarea
                value={form.description}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    description: event.target.value
                  }))
                }
                className="min-h-[88px] w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                placeholder="What this role is used for"
              />
            </label>

            <div>
              <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-sm font-medium text-slate-700">
                  Accessible areas
                </span>
                {isEditingSuperAdmin && (
                  <span className="text-xs text-slate-500">
                    Super admin access is always enabled.
                  </span>
                )}
              </div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {editablePermissionKeys.map((permission) => {
                  const checked =
                    isEditingSuperAdmin ||
                    form.permissions.includes(permission);
                  return (
                    <button
                      key={permission}
                      type="button"
                      onClick={() => togglePermission(permission)}
                      disabled={isEditingSuperAdmin}
                      className={`flex items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm transition ${
                        checked
                          ? 'border-sky-200 bg-[#eef9ff] text-[#0b1f3a]'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-sky-100 hover:bg-slate-50'
                      } disabled:cursor-not-allowed`}
                    >
                      <span>{getPermissionLabel(permission)}</span>
                      {checked && (
                        <CheckCircle2 size={16} className="text-sky-600" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </form>
        )}
      </RbacModal>
    </div>
  );
}

export default RolesPage;
